import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

/**
 * ShareToCommonsModal — share one library item to The Commons feed.
 * Optional note (≤500 chars) + optional label. On send, POSTs to
 * /api/commons/posts. If the user hasn't claimed a username yet the backend
 * returns 400 and we bounce them to /commons where the claim modal opens.
 */
const MAX = 500;

const LABEL_OPTIONS = [
  { key: null,               name: "no label" },
  { key: "just-discovered",  name: "just discovered" },
  { key: "just-finished",    name: "just finished" },
  { key: "tried-it",         name: "tried it" },
  { key: "revisiting",       name: "revisiting" },
  { key: "added",            name: "just added" },
];

export default function ShareToCommonsModal({ item, onClose, onShared }) {
  const [note, setNote] = useState("");
  const [label, setLabel] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Hide the global Add FAB while the modal is open.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  async function send() {
    if (sending) return;
    setSending(true);
    try {
      await api.post("/commons/posts", {
        item_id: item.id,
        note: note.trim().slice(0, MAX),
        label,
      });
      toast.success("shared to the commons");
      onShared?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "could not share";
      const status = e?.response?.status;
      toast.error(detail);
      // Backend returns 400 with a "Claim a username…" detail when the user
      // hasn't claimed one yet — walk them to /commons where the mandatory
      // claim modal pops. Gated on status===400 (not fuzzy string match) so
      // future copy tweaks don't break this redirect.
      if (status === 400 && typeof detail === "string" && detail.toLowerCase().includes("username")) {
        navigate("/commons");
        onClose?.();
      }
    } finally {
      setSending(false);
    }
  }

  const remaining = MAX - note.length;
  const over = remaining < 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-[rgba(0,0,0,0.7)] backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-commons-modal-backdrop"
    >
      <div
        className="w-full max-w-md placed-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="share-commons-modal"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="share-commons-close"
          className="absolute top-3 right-3 text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
          aria-label="close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          share to the commons
        </p>
        <h2 className="mt-2 font-serif-lit text-2xl text-[var(--cream)] leading-tight">
          {item.title || "this item"}
        </h2>
        {item.creator && (
          <p className="font-garamond italic text-[var(--cream-dim)]">by {item.creator}</p>
        )}

        <div className="mt-6">
          <span className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-dim)]">
            label — optional
          </span>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="share-commons-labels">
            {LABEL_OPTIONS.map(opt => {
              const active = label === opt.key;
              return (
                <button
                  key={opt.key || "none"}
                  type="button"
                  onClick={() => setLabel(opt.key)}
                  data-testid={`share-commons-label-${opt.key || "none"}`}
                  className={`chip ${active ? "active" : ""}`}
                >
                  {opt.name}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block mt-6">
          <span className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-dim)]">
            a note — optional
          </span>
          <textarea
            ref={inputRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="why do you love it?"
            rows={4}
            data-testid="share-commons-note"
            className="mt-2 w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded p-3 font-garamond italic text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)] resize-none"
          />
        </label>
        <p
          className={`mt-1 font-mono-lc text-[10px] tracking-[0.14em] text-right ${over ? "text-[#C86A5C]" : "text-[var(--cream-mute)]"}`}
          data-testid="share-commons-counter"
        >
          {remaining} left
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="share-commons-cancel" className="lc-btn-ghost">
            never mind
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || over}
            data-testid="share-commons-send"
            className="lc-btn"
          >
            {sending ? "sharing…" : "share"}
          </button>
        </div>
      </div>
    </div>
  );
}
